import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createCarbonApi from 'api/carbonOffset';
import createCarLoansApi from 'api/carLoans';
import createEmployeeBenefitApi from 'api/employeeBenefit';
import axios, { CancelTokenSource } from 'axios';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { cloneDeep } from 'lodash-es';
import { appContextSelector } from 'modules/app/appSlice';
import { createSubscription, updateSubscription } from 'modules/subscriptions/subscriptionSlice';
import { syncAttribution } from 'modules/user/attributionSlice';
import { currentUserSelector } from 'modules/user/userSlice';
import { AppThunk, RootState } from 'store';
import { setTag, tags } from 'utils/tags';
import { roundNumberToTwo } from 'utils/numberFormatter';

const CancelToken = axios.CancelToken;
let source: CancelTokenSource = CancelToken.source();

type ProcessType = 'create' | 'update' | 'cancel';
type ProcessSubType = 'normal' | 'upload';

interface State {
  items: SubscriptionItem[];
  clonedItems: SubscriptionItem[];
  isLoading: boolean;
  currency: string;
  error?: string;
  errorType?: string;
  subscriptionId?: string;
  summary: boolean;
  process: ProcessType;
  currentStep: string;
  cancelAtPeriodEnd?: boolean;
  latestOrderDate?: string;
  processFile?: boolean;
  fileUploading: boolean;
  fileError: string[];
  fileSuccess: boolean;
  subType: ProcessSubType;
  attachmentTempUrl?: string;
  attachment?: SubscriptionAttachment;
}

interface AttachmentProps {
  url: string;
  attachment: SubscriptionAttachment;
}

interface CountryProps {
  index: number;
  area: string;
}

interface NumberOfCarsProps {
  index: number;
  numberOfCars: number;
}

interface CreateProps {
  numberOfCars?: number;
  area?: string;
  kilosCo2?: number;
  price?: number;
  callback?: () => void;
}

interface UpdateProps {
  index: number;
  numberOfCars?: number;
  area?: string;
  kilosCo2: number;
  price: number;
}

interface TempProps {
  area: string;
  cars: number;
  callback: (tonnes: number, priceData: number) => void;
}

export const initialState: State = {
  items: [],
  clonedItems: [],
  isLoading: true,
  currency: 'EUR',
  error: '',
  summary: false,
  process: 'create',
  cancelAtPeriodEnd: false,
  currentStep: 'carLoansPlan',
  processFile: true,
  fileUploading: false,
  fileError: [],
  fileSuccess: false,
  subType: 'normal',
};

const slice = createSlice({
  name: 'carLoans',
  initialState,
  reducers: {
    addItem: (state: State, action: PayloadAction<CreateProps>): State => {
      const { numberOfCars, area, kilosCo2, price } = action.payload;

      const newItem = {
        portfolioid: 'defaultco2',
        kilosCo2: kilosCo2 || 0,
        price: price || 0,
        details: {
          car: {
            area: area || 'EU',
            cars: numberOfCars || 1,
          },
        },
      };

      state.items.push(newItem);
      return state;
    },

    removeItem: (state: State, action: PayloadAction<number>): State => {
      state.items.splice(action.payload, 1);
      return state;
    },

    updateItem: (state: State, action: PayloadAction<UpdateProps>): State => {
      const { index, area, kilosCo2, price, numberOfCars } = action.payload;
      const item = state.items[index];

      if (item.details.car) {
        item.details.car.area = area ?? 'EU';
        item.details.car.cars = numberOfCars ?? 1;
      }
      item.kilosCo2 = kilosCo2;
      item.price = price;

      return state;
    },

    refreshItems: (state: State): State => {
      state.items = cloneDeep(state.clonedItems);
      return state;
    },

    setProcess: (state: State, action: PayloadAction<ProcessType | undefined>): State => {
      if (action.payload) {
        state.process = action.payload;
        return state;
      }

      if (state.subscriptionId) {
        if (state.items.length) {
          state.process = 'update';
        } else {
          state.process = 'cancel';
        }
      } else {
        state.process = 'create';
      }

      return state;
    },

    setCurrency: (state: State, action: PayloadAction<string>): State => {
      state.currency = action.payload;
      return state;
    },

    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },

    setError: (state: State, action: PayloadAction<string>): State => {
      state.error = action.payload;
      return state;
    },

    setErrorType: (state: State, action: PayloadAction<string>): State => {
      state.errorType = action.payload;
      return state;
    },

    clearCarLoans: (): State => {
      return initialState;
    },

    setCarLoans: (state: State, action: PayloadAction<SubscriptionObject>): State => {
      state.items = action.payload.items;
      state.clonedItems = cloneDeep(state.items);
      state.subscriptionId = action.payload.id;
      state.currency = action.payload.currency ?? 'usd';
      state.latestOrderDate = action.payload.latestOrderDate ?? '';
      state.cancelAtPeriodEnd = action.payload.cancelAtPeriodEnd ?? false;
      state.attachment = action.payload.attachments?.[0] ?? undefined;
      return state;
    },

    setCarLoansAfterFileUpload: (state: State, action: PayloadAction<SubscriptionObject>): State => {
      state.clonedItems = cloneDeep(state.items);
      state.items = action.payload.items;
      state.currency = action.payload.currency ?? 'usd';
      return state;
    },

    setCurrentStep: (state: State, action: PayloadAction<string>): State => {
      state.currentStep = action.payload;
      return state;
    },

    setFileUploading: (state: State, action: PayloadAction<boolean>): State => {
      state.fileUploading = action.payload;
      return state;
    },

    setFileError: (state: State, action: PayloadAction<string[]>): State => {
      state.fileError = action.payload;
      return state;
    },

    setFileSuccess: (state: State, action: PayloadAction<boolean>): State => {
      state.fileSuccess = action.payload;
      return state;
    },

    setFileProcessing: (state: State, action: PayloadAction<boolean>): State => {
      if (!action.payload) {
        source.cancel();
      }
      state.processFile = action.payload;
      state.fileUploading = action.payload;
      return state;
    },

    setSubType: (state: State, action: PayloadAction<ProcessSubType>): State => {
      state.subType = action.payload;
      return state;
    },

    setAttachment: (state: State, action: PayloadAction<AttachmentProps>): State => {
      state.attachmentTempUrl = action.payload.url;
      state.attachment = action.payload.attachment;
      return state;
    },
  },
});

const {
  addItem,
  updateItem,
  setLoading,
  clearCarLoans,
  setError,
  setCurrency,
  setCurrentStep,
  setCarLoans,
  setProcess,
  setErrorType,
  removeItem,
  refreshItems,
  setFileUploading,
  setFileProcessing,
  setFileSuccess,
  setFileError,
  setCarLoansAfterFileUpload,
  setSubType,
  setAttachment,
} = slice.actions;

export { removeItem, refreshItems, setCurrentStep, setProcess, setFileUploading, setFileProcessing, setFileSuccess, setFileError, setSubType };

export const isDetailsCorrect = (items: SubscriptionItem[]): boolean => items.every(item => item.details && item.details.car);

export const normalizePriceQuote = (subscription: SubscriptionObject): SubscriptionObject => ({
  ...subscription,
  items: subscription.items.map(item => {
    return {
      ...item,
      details: {
        car: {
          area: '',
          cars: item.feeFactors?.carMonthlyCars ?? 0,
        },
      },
    };
  }),
});

export const normalizeItems = (items: SubscriptionItem[]): SubscriptionItem[] =>
  items.map(item => {
    return {
      portfolioid: item.portfolioid || 'defaultco2',
      kilosCo2: item.kilosCo2,
      price: item.price,
      details: {
        car: item.details.car,
      },
      feeFactors: {
        carMonthlyCars: item.details.car?.cars ?? 0,
      },
    };
  });

export const saveSubscription = (cb?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const { items, clonedItems, subscriptionId, process, attachment, attachmentTempUrl } = getState().carLoans;
  const carsAmount = items.reduce<number>((prev, next) => (prev += next.details.car?.cars ?? 0), 0);
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const normalizedItems = normalizeItems(items);
  const normalizedClonedItems = normalizeItems(clonedItems);
  const context = appContextSelector(getState());
  const employeeBenefitAPI = createEmployeeBenefitApi(context);

  try {
    if (process === 'update') {
      if (subscriptionId) {
        const data = await employeeBenefitAPI.update(
          subscriptionId,
          {
            interval: 'monthly',
            category: 'Car loan',
            description: `Car loan for ${carsAmount} cars`,
            items: normalizedItems.length ? normalizedItems : normalizedClonedItems,
            attachments: attachmentTempUrl && attachment ? [attachment] : [],
          },
          currentUser?.customerId ?? '',
        );
        dispatch(updateSubscription({ ...data.subscription }));
        dispatch(syncAttribution());
      }
    } else if (process === 'cancel') {
      if (subscriptionId) {
        const data = await employeeBenefitAPI.remove(subscriptionId, currentUser?.customerId ?? '');
        dispatch(updateSubscription({ ...data.subscription }));
        dispatch(syncAttribution());
      }
    } else {
      const subscription = {
        type: 'Car',
        interval: 'monthly',
        category: 'Car loan',
        description: `Car loan for ${carsAmount} cars`,
        items: normalizedItems,
        attachments: attachmentTempUrl && attachment ? [attachment] : [],
      };
      const data = await employeeBenefitAPI.create(subscription, currentUser?.customerId ?? '');
      dispatch(createSubscription({ ...data.subscription }));
      dispatch(syncAttribution());
      setTag({
        ...tags.portalOffset.placeOrder,
        categories: [data.subscription?.category ?? ''],
        transactionId: data.orderId,
        transactionTotal: data.subscription.totalPrice,
        currencyCode: data.subscription.currency,
        transactionProducts: data.subscription.items.map(item => ({
          category: data.subscription.category ?? '',
          name: 'Car loan offset',
          price: roundNumberToTwo(item.price),
          quantity: item.kilosCo2,
          sku: 'car-loan-offset',
        })),
      });
    }

    cb && cb();
  } catch (err) {
    dispatch(setError(process !== 'create' ? 'Unable to update a subscription plan.' : 'Unable to create a subscription plan.'));
  } finally {
    dispatch(setLoading(false));
  }
};

export const addNewItem = (props?: CreateProps, cb?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const carLoansAPI = createCarLoansApi(context);
  dispatch(setLoading(true));
  const area = props?.area ?? 'EU';
  const numberOfCars = props?.numberOfCars ?? 1;
  const currentUser = currentUserSelector(getState());

  try {
    const footprintData = await carLoansAPI.getCarFootprint(area, numberOfCars);
    const kilosCo2 = footprintData.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2,
        carMonths: numberOfCars,
      },
      currentUser?.customerId ?? '',
    );
    dispatch(addItem({ area, kilosCo2, price: priceData.salesPrice, numberOfCars }));
    cb && cb();
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setError('Unable to add new subscription item.'));
  }
};

export const updateItemCars = (props: NumberOfCarsProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const carLoansAPI = createCarLoansApi(context);
  dispatch(setLoading(true));
  const { items } = getState().carLoans;
  const item = items[props.index];
  const currentUser = currentUserSelector(getState());
  const numberOfCars = props.numberOfCars ?? 1;
  const area = item.details.car?.area ?? 'EU';

  try {
    const data = await carLoansAPI.getCarFootprint(area, numberOfCars);
    const kilosCo2 = data.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2,
        carMonths: numberOfCars,
      },
      currentUser?.customerId ?? '',
    );
    dispatch(updateItem({ index: props.index, kilosCo2, price: priceData.salesPrice, numberOfCars, area }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setErrorType('country'));
    dispatch(setError('Unable to update country for subscription item.'));
  }
};

export const updateItemCountry = (props: CountryProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const carLoansAPI = createCarLoansApi(context);
  dispatch(setLoading(true));
  const { items } = getState().carLoans;
  const item = items[props.index];
  const currentUser = currentUserSelector(getState());
  const numberOfCars = item.details.car?.cars ?? 1;

  try {
    const data = await carLoansAPI.getCarFootprint(props.area, numberOfCars);
    const kilosCo2 = data.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2,
        carMonths: numberOfCars,
      },
      currentUser?.customerId ?? '',
    );
    dispatch(updateItem({ ...props, index: props.index, kilosCo2, price: priceData.salesPrice, numberOfCars }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setErrorType('country'));
    dispatch(setError('Unable to update country for subscription item.'));
  }
};

export const prepareCarLoans = (subscription: SubscriptionObject | undefined, fileUploading = false): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  !fileUploading && dispatch(clearCarLoans());
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);

  try {
    if (subscription) {
      const normalizedSubscription = cloneDeep(subscription);
      if (!fileUploading) {
        for (const item of normalizedSubscription.items) {
          const data = await carbonApi.getCarbonOffsetPrice(
            {
              kilosCo2: item.kilosCo2,
              carMonths: item.details.car?.cars ?? 1,
            },
            currentUser?.customerId ?? '',
          );
          item.price = data.salesPrice;
        }
        dispatch(setCarLoans(normalizedSubscription));
      } else {
        dispatch(setCarLoansAfterFileUpload(normalizedSubscription));
      }

      if (normalizedSubscription.cancelAtPeriodEnd) {
        dispatch(setCurrentStep('cancelPlan'));
      }
    } else {
      const currency = currentUser?.customer?.payment?.currency ?? 'usd';
      dispatch(setCurrency(currency));
    }
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setError('Unable to fetch price for subscription items.'));
  }
};

export const getCO2ForTemporaryItem = (props: TempProps): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const { area, cars, callback } = props;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const carLoansAPI = createCarLoansApi(context);

  try {
    const data = await carLoansAPI.getCarFootprint(area, cars);
    const kilosCo2 = data.kilosCo2e;
    const priceData = await carbonApi.getCarbonOffsetPrice(
      {
        kilosCo2,
        carMonths: cars,
      },
      currentUser?.customerId ?? '',
    );
    callback(kilosCo2, priceData.salesPrice);
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setErrorType('country'));
    dispatch(setError('Unable to fetch amount of CO2 for subscription item.'));
  }
};

export const processFile = (file: File): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setFileProcessing(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carLoansAPI = createCarLoansApi(context);
  try {
    source = CancelToken.source();
    const response = await carLoansAPI.processCarBatch(currentUser?.customerId ?? 'default', file, source.token);
    if (getState().carLoans.processFile) {
      if (response.priceQuote) {
        dispatch(setAttachment({ url: response.attachmentTempUrl, attachment: response.attachment }));
        dispatch(prepareCarLoans(normalizePriceQuote(response.priceQuote), true));
        dispatch(setFileSuccess(true));
      } else {
        dispatch(setFileError(response.data));
      }
    }

    dispatch(setFileUploading(false));
  } catch (err) {
    if (err && err.data && err.data.length) {
      const { data } = err;
      dispatch(setFileError(typeof data === 'string' ? [data] : [...data]));
    }
    dispatch(setFileUploading(false));
  }
};

export const refreshState = (cb?: CallableFunction): AppThunk => async (dispatch): Promise<void> => {
  dispatch(refreshItems());
  cb && cb();
};

export const downloadOrderAttachment = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const carLoansAPI = createCarLoansApi(context);
  const { subscriptionId, attachment } = getState().carLoans;
  const currentUser = currentUserSelector(getState());
  const fileName = `${currentUser?.customer?.name ?? ''}_cars_output_${format(new Date(), 'yyyy.MM.dd')}.xlsx`;

  try {
    if (attachment && subscriptionId) {
      const blob = await carLoansAPI.downloadOrderAttachment(currentUser?.customerId ?? 'default', subscriptionId, attachment.storageBlobName);
      saveAs(blob, fileName);
    }
  } catch {
    //err
  }
};

// @TODO: Update this function when backend will be ready
export const downloadTemporaryAttachment = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const { attachmentTempUrl } = getState().carLoans;
  const currentUser = currentUserSelector(getState());
  const fileName = `${currentUser?.customer?.name ?? ''}_cars_output_${format(new Date(), 'yyyy.MM.dd')}.xlsx`;

  try {
    if (attachmentTempUrl) {
      const response = await fetch(attachmentTempUrl).then(response => response.blob());
      const blob = new Blob([response], { type: 'application/octet-stream' });
      saveAs(blob, fileName);
    }
  } catch {
    //err
  }
};

export default slice.reducer;

export const carLoansAmountOfCars = createSelector(
  (state: RootState) => state.carLoans.items,
  items => items.reduce<number>((prev, next) => (prev += next.details.car?.cars ?? 0), 0),
);

export const carLoansTotalKilosCO2 = createSelector(
  (state: RootState) => state.carLoans.items,
  items => items.reduce<number>((prev, next) => (prev += next.kilosCo2), 0),
);

export const carLoansTotalPrice = createSelector(
  (state: RootState) => state.carLoans.items,
  items => items.reduce<number>((prev, next) => (prev += next.price), 0),
);

export const carLoansTotalKilosCO2FromCopy = createSelector(
  (state: RootState) => state.carLoans.clonedItems,
  items => items.reduce<number>((prev, next) => (prev += next.kilosCo2), 0),
);

export const carLoansTotalPriceFromCopy = createSelector(
  (state: RootState) => state.carLoans.clonedItems,
  items => items.reduce<number>((prev, next) => (prev += next.price), 0),
);
